"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { authFetch } from "@/lib/authFetch";

export type BridgeDirection =
  | "polygon_to_bsc" | "bsc_to_polygon"
  | "polygon_to_solana" | "solana_to_polygon"
  | "bsc_to_solana" | "solana_to_bsc"
  | "polygon_to_arbitrum" | "arbitrum_to_polygon"
  | "arbitrum_to_solana" | "solana_to_arbitrum";

interface BridgeQuote {
  estimatedReceive: number;
  estimatedFee: number;
  estimatedTimeMinutes: number;
}

export interface BridgeStatus {
  id: string;
  status: "pending" | "approved" | "bridging" | "completed" | "failed";
  amount_from: number;
  amount_to: number | null;
  direction: string;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  // LI.FI status fields
  sendingTxHash?: string;
  sendingTxLink?: string;
  receivingTxHash?: string;
  receivingTxLink?: string;
  substatus?: string;
  substatusMessage?: string;
}


export function useBridge() {
  const [quote, setQuote] = useState<BridgeQuote | null>(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [bridgeStatus, setBridgeStatus] = useState<BridgeStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const getQuote = useCallback(async (amount: number, direction: BridgeDirection) => {
    setIsQuoting(true);
    setError(null);
    try {
      const res = await authFetch("/api/bridge/cross-chain/quote", {
        method: "POST",
        body: JSON.stringify({ amount, direction }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Quote failed");
      setQuote(data.quote);
      return data.quote as BridgeQuote;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setIsQuoting(false);
    }
  }, []);

  const execute = useCallback(async (amount: number, direction: BridgeDirection) => {
    setIsExecuting(true);
    setError(null);
    setBridgeStatus(null);
    try {
      const res = await authFetch("/api/bridge/cross-chain/execute", {
        method: "POST",
        body: JSON.stringify({ amount, direction }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Bridge failed");

      const bridgeId = data.bridgeId;
      const bridgeTxHash = data.bridgeTxHash as string | undefined;

      setBridgeStatus({
        id: bridgeId,
        status: "approved",
        amount_from: amount,
        amount_to: data.estimatedReceive,
        direction,
        error_message: null,
        created_at: new Date().toISOString(),
        completed_at: null,
        sendingTxHash: bridgeTxHash,
        substatus: "WAIT_DESTINATION_TRANSACTION",
        substatusMessage: "Waiting for bridge to complete...",
      });

      // Poll the BACKEND status endpoint — source of truth.
      // The backend's bridge-lifi-monitor already polls LI.FI server-side and
      // updates bridge_transfers, so we don't need to hit li.quest from the
      // browser (CORS / rate-limit issues swallowed prior completions).
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await authFetch(`/api/bridge/cross-chain/status/${bridgeId}`);
          const statusData = await statusRes.json();
          if (!statusData.success || !statusData.bridge) return;

          const b = statusData.bridge;
          setBridgeStatus(prev => ({
            id: b.id,
            status: b.status,
            amount_from: Number(b.amount_from ?? prev?.amount_from ?? amount),
            amount_to: b.amount_to != null ? Number(b.amount_to) : (prev?.amount_to ?? null),
            direction: b.direction ?? direction,
            error_message: b.error_message ?? null,
            created_at: b.created_at ?? prev?.created_at ?? new Date().toISOString(),
            completed_at: b.completed_at ?? null,
            sendingTxHash: b.bridge_tx_hash ?? prev?.sendingTxHash,
            sendingTxLink: prev?.sendingTxLink,
            receivingTxHash: b.receiving_tx_hash ?? prev?.receivingTxHash,
            receivingTxLink: prev?.receivingTxLink,
            substatus: prev?.substatus,
            substatusMessage:
              b.status === "completed" ? "Transfer complete!" :
              b.status === "failed" ? (b.error_message || "Bridge failed") :
              b.status === "bridging" ? "Bridging cross-chain — almost there..." :
              prev?.substatusMessage,
          }));

          if (b.status === "completed" || b.status === "failed") {
            if (pollingRef.current) clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        } catch {
          // Silent — keep polling
        }
      }, 5000); // 5s — backend already has live state from its own LI.FI monitor

      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setIsExecuting(false);
    }
  }, []);

  const reset = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = null;
    setQuote(null);
    setBridgeStatus(null);
    setError(null);
    setIsQuoting(false);
    setIsExecuting(false);
  }, []);

  return {
    quote,
    getQuote,
    execute,
    bridgeStatus,
    isQuoting,
    isExecuting,
    error,
    reset,
  };
}
